import * as Yup from "yup";
import { format } from "date-fns";
import type { DurationOption } from "../../../../../../../components/DurationPopper/utils/constants";
import {
  NATIVE_ARRAY_FIELDS,
  NATIVE_STRING_FIELDS,
  DURATION_OPTIONS,
  HELPDESK_FILTER_BETWEEN_POPPER_OPTIONS,
  VALUE_OPTIONS_MAP,
  type ViewFormValues,
} from "./constants";
import type { FilterGroup } from "../../../../../../../redux/services/helpdeskApi";
import type { HelpdeskCustomFieldDefinition } from "../../TicketDetails/components/TicketOverview/utils/helpdeskCustomFields";
import { assignDatesToDuration } from "../../../../../../../components/DurationPopper/utils/dateUtils";
import { filterActiveCampaignUsers } from "../../../utils/function";

export const viewFormValidationSchema = Yup.object({
  name: Yup.string().required("View name is required").trim(),
  scope: Yup.string()
    .oneOf(["private", "public"], "Please select who can access this view")
    .required("Please select who can access this view"),
  filter_groups: Yup.array(),
  logical_operator: Yup.string().oneOf(["and", "or"]).required(),
});

export function getConditionValue(
  condition: { key: string; comparison_operator: string; value: string[] },
  customFields?: HelpdeskCustomFieldDefinition[]
) {
  const { key, comparison_operator, value } = condition;

  if (comparison_operator === "exists") return value?.[0] === "true";

  // Value-list fields (assignees, priority, tags, …) keep array payloads for
  // every operator, including contains / not_contains / contains_any / not_in.
  if (NATIVE_ARRAY_FIELDS.includes(key)) return value;
  if (key === "request_support") return value?.[0] === "true";
  if (NATIVE_STRING_FIELDS.includes(key)) return value?.[0];
  if (key === "duration") {
    if (comparison_operator === "range" && (value?.length ?? 0) >= 2) {
      return [value[0], value[1]];
    }
    return value?.[0];
  }

  const fieldType = customFields?.find((f) => f.key === key)?.type;

  if (fieldType === "dropdown_multi_select" || fieldType === "checkbox") {
    return value;
  }
  if (fieldType === "number") {
    return comparison_operator === "range"
      ? (value ?? []).map((v) => Number(v))
      : Number(value?.[0]);
  }
  // Custom date "Between" only — Equals keeps the same payload shape as before (single value via fallthrough)
  if (
    fieldType === "date" &&
    comparison_operator === "range" &&
    (value?.length ?? 0) >= 2
  ) {
    return [value[0], value[1]];
  }
  return value?.[0];
}

const FILTER_PRESET_KEY_TO_POPPER_ID: Record<string, string> = {
  last_7_days: "last-7-days",
  last_30_days: "last-30-days",
  last_3_months: "last-3-months",
  last_6_months: "last-6-months",
  last_1_year: "last-1-year",
};

export function getDurationPopperInitialForFilter(value: string[]): {
  initialDates?: { from: Date; to: Date };
  initialOption?: DurationOption;
} {
  if (!value?.length) return {};
  const [first, second, optionId] = value;
  if (
    first &&
    second &&
    !Number.isNaN(Date.parse(first)) &&
    !Number.isNaN(Date.parse(second))
  ) {
    const matchedOption =
      optionId && optionId !== "custom"
        ? (HELPDESK_FILTER_BETWEEN_POPPER_OPTIONS.find(
            (o) => o.id === optionId
          ) as DurationOption | undefined)
        : undefined;
    return {
      initialDates: { from: new Date(first), to: new Date(second) },
      ...(matchedOption ? { initialOption: matchedOption } : {}),
    };
  }
  const popperId = first ? FILTER_PRESET_KEY_TO_POPPER_ID[first] : undefined;
  if (popperId) {
    const initialOption = HELPDESK_FILTER_BETWEEN_POPPER_OPTIONS.find(
      (o) => o.id === popperId
    );
    if (initialOption)
      return { initialOption: initialOption as DurationOption };
    const legacyRange = assignDatesToDuration(popperId);
    if (legacyRange) {
      return {
        initialDates: {
          from: legacyRange.from,
          to: legacyRange.to,
        },
      };
    }
  }
  return {};
}

export function formatFilterBetweenValueDisplay(value: string[]): string {
  const [a, b] = value ?? [];
  if (a && b && !Number.isNaN(Date.parse(a)) && !Number.isNaN(Date.parse(b))) {
    return `${format(new Date(a), "dd MMM yyyy")} – ${format(
      new Date(b),
      "dd MMM yyyy"
    )}`;
  }
  if (a) {
    const preset = DURATION_OPTIONS.find((d) => d.key === a);
    if (preset) return preset.label;
  }
  return "";
}

export function buildFilterGroupsPayload(
  filterGroups: ViewFormValues["filter_groups"],
  customFields?: HelpdeskCustomFieldDefinition[]
): FilterGroup[] {
  return (filterGroups ?? [])
    .filter((group) =>
      group?.conditions?.some((c) => c?.key && c?.value?.length > 0)
    )
    .map((group, index) => ({
      conditions: (group?.conditions ?? [])
        .filter((c) => c?.key && c?.value?.length > 0)
        .map((c) => ({
          key: c.key,
          comparison_operator: c.comparison_operator,
          value: getConditionValue(
            c as { key: string; comparison_operator: string; value: string[] },
            customFields
          ),
          is_custom_field: c?.is_custom_field ?? false,
        })),
      logical_operator: group?.logical_operator ?? "and",
    })) as FilterGroup[];
}

export function parseAgentOptions(
  allAgentsData:
    | { items?: { _id: string; name: string; status?: string }[] }
    | undefined
): { key: string; label: string }[] {
  if (!allAgentsData?.items) return [];
  const seen = new Set<string>();
  return filterActiveCampaignUsers(allAgentsData.items)
    .filter((agent) => {
      if (seen?.has(agent._id)) return false;
      seen?.add(agent._id);
      return true;
    })
    .map((agent) => ({
      key: agent._id,
      label: agent.name,
    }));
}

export function parseTagOptions(
  helpdeskTagsData: { tag_key: string; title: string }[] | undefined
): { key: string; label: string }[] {
  if (!helpdeskTagsData) return [];
  return helpdeskTagsData?.map((tag) => ({
    key: tag.tag_key,
    label: tag.title,
  }));
}

export function parseStageOptions(
  stages: { key: string; title?: string }[] | undefined
): { key: string; label: string }[] {
  if (!stages) return [];
  return stages?.map((stage) => ({
    key: stage.key,
    label: stage.title || stage.key,
  }));
}

export function buildStageColorMap(
  stages: { key: string; secondary_hex_code: string }[] | undefined
): Record<string, string> {
  if (!stages) return {};
  const map: Record<string, string> = {};
  stages?.forEach((stage) => {
    map[stage.key] = stage.secondary_hex_code;
  });
  return map;
}

export function parseUserOptions(
  users: { _id: string; name: string }[] | undefined
): { key: string; label: string }[] {
  if (!users?.length) return [];
  const seen = new Set<string>();
  return users
    .filter((user) => {
      if (seen?.has(user._id)) return false;
      seen?.add(user._id);
      return true;
    })
    .map((user) => ({
      key: user._id,
      label: user.name,
    }));
}

export function parsePropertyOptions(
  propertiesData: { id: number; name: string }[] | undefined
): { key: string; label: string }[] {
  if (!propertiesData) return [];
  return propertiesData?.map((p) => ({
    key: p.name,
    label: p.name,
  }));
}

export function getValueOptionsForKey(
  key: string,
  agentOptions: { key: string; label: string }[],
  tagOptions: { key: string; label: string }[],
  stageOptions: { key: string; label: string }[],
  propertyOptions: { key: string; label: string }[],
  userOptions: { key: string; label: string }[],
  customFieldDefinitions?: HelpdeskCustomFieldDefinition[],
  resolutionOptions?: { key: string; label: string }[],
  ticketCategoryOptions?: { key: string; label: string }[]
): { key: string; label: string }[] {
  if (key === "assignees") return agentOptions;
  if (key === "tags") return tagOptions;
  if (key === "stage_key") return stageOptions;
  if (key === "property_name") return propertyOptions;
  if (key === "created_by") return userOptions;
  if (key === "resolution_reason") return resolutionOptions ?? [];
  if (key === "ticket_category") return ticketCategoryOptions ?? [];

  const staticOptions = VALUE_OPTIONS_MAP?.[key];
  if (staticOptions) return staticOptions;

  if (customFieldDefinitions?.length) {
    const customField = customFieldDefinitions.find((f) => f.key === key);
    if (!customField) return [];

    if (customField?.value_map && typeof customField.value_map === "object") {
      const allValues = Object.values(customField.value_map).flat();
      const unique = [...new Set(allValues)];
      return unique.map((v) => ({ key: v, label: v }));
    }

    if (customField?.values_for_dropdown?.length) {
      return customField.values_for_dropdown.map((v) => ({
        key: v,
        label: v,
      }));
    }
  }

  return [];
}
